/**
 * How far the week has to scroll sideways so the day that just opened is not under the panel.
 *
 * ── The problem this answers ────────────────────────────────────────────────────────────────
 * The 574px day panel OVERLAYS the week rather than pushing it (see `WeekPane`'s header). Clicking
 * the last training day of a full week therefore leaves `scrollLeft` at 0 with the day just opened
 * entirely behind the panel — its selection ring included. The mock does not solve this, because a
 * static prototype has one scroll position; the owner asked for it (7 September 2026).
 *
 * The week itself always FITS (KANON: "tydzień zawsze mieści się na ekranie"), so the room to scroll
 * into is grown by `.week[data-panel-room]` while the panel is open, out of flow, exactly the panel's
 * width. This module does not know that: it is handed a scrollport and a column and answers with a
 * delta, and where the range came from is the caller's business.
 *
 * ── The rule ────────────────────────────────────────────────────────────────────────────────
 * The strip the trainer can actually see runs from `scrollerLeft` to `scrollerRight -
 * obstructionWidth`, and the column has to end up inside it. Both edges matter, and an earlier
 * revision answered only for the right one: it returned `0` whenever the column was not overhanging,
 * which is exactly the case where a day sits off-screen to the LEFT. Stepping forward through a week
 * worked and stepping back did nothing — reported 8 September 2026.
 *
 * So: if the column starts before the visible strip, scroll LEFT until it does not (a negative
 * delta). Otherwise, if its right edge plus a gutter passes the far end, scroll right by the
 * overhang — but never so far that the column's own left edge leaves the scrollport. A training
 * column can be 496px against a narrower free strip, and when it cannot fit, the half that wins is
 * the one carrying the weekday, the grip and the workout's name: that is what tells a trainer which
 * day is open.
 *
 * The left case is checked FIRST for the same reason it wins the clamp on the right: a column too
 * wide for the strip can satisfy neither edge, and the left edge is the one worth having.
 *
 * Every coordinate is in the same space (the viewport's, as `getBoundingClientRect` reports it) and
 * the answer is a DELTA to add to `scrollLeft`, so this module never learns what a DOM node is.
 * `0` means there is nothing to do.
 */

export interface RevealScrollInput {
  /** The scrollport's own edges — not the content's. */
  readonly scrollerLeft: number;
  readonly scrollerRight: number;
  readonly columnLeft: number;
  readonly columnRight: number;
  /**
   * How much of the scrollport's right end is covered.
   *
   * The panel's `offsetWidth`, never a rect — see `useRevealOpenDay`. `0` is a legitimate value: it
   * is what a caller with nothing over the week passes, and the rule degrades to plain
   * "scroll it into view".
   */
  readonly obstructionWidth: number;
  /** The breathing room left between the column's right edge and the panel's left edge. */
  readonly gutter: number;
}

export function revealScrollDelta({
  scrollerLeft,
  scrollerRight,
  columnLeft,
  columnRight,
  obstructionWidth,
  gutter,
}: RevealScrollInput): number {
  // BEFORE the visible strip: scroll back. Negative, because the caller adds this to `scrollLeft`.
  // The gutter applies on this side too, so a revealed column is not flush against the edge.
  const shortfall = columnLeft - gutter - scrollerLeft;
  if (shortfall < 0) return shortfall;

  const freeRight = scrollerRight - obstructionWidth;
  const overhang = columnRight + gutter - freeRight;
  if (overhang <= 0) return 0;

  // What is left before the column's own left edge would pass out of the scrollport. Zero or less
  // when the column already starts at the edge, and scrolling further would trade a visible left
  // edge for a visible right one.
  const room = columnLeft - scrollerLeft;
  if (room <= 0) return 0;

  return Math.min(overhang, room);
}

/**
 * What covers the canvas's right-hand end, and so how much trailing room it grows: the day panel
 * (574px), the Buddy panel (448px), or both side by side (owner, 7 October 2026). `null` while
 * neither is open: then the week has no room to scroll into.
 */
export type OverlayRoom = 'day' | 'assistant' | 'both' | null;

export function overlayRoom(dayPanelOpen: boolean, assistantOpen: boolean): OverlayRoom {
  if (dayPanelOpen) return assistantOpen ? 'both' : 'day';
  return assistantOpen ? 'assistant' : null;
}
